import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { APP_ENV, DEMO_SURFACES_IN_BUILD } from "@/lib/env";
import { Badge } from "@/components/ui/badge";
import { BrandFooter } from "@/components/BrandHeader";
import { CoBrandedHeader } from "@/components/CoBrandedHeader";
import { StaffOrgBanner } from "@/components/StaffOrgBanner";
import { REVIEW_PAGES } from "@/demoReviewPages";
import { CookieConsent } from "@/components/CookieConsent";
import { syncClarityWithRoute } from "@/lib/clarity";
import { useTenant } from "@/hooks/useTenant";
import { useOptionalAuth } from "@/contexts/AuthContext";
import { demoPartnerKeyForPath } from "@/lib/demoSurfaces";
import { flowLookFor, isPartnerLookKey, isSessionJourneyPath } from "@/lib/flowPresentation";
import { SessionVerticalPublishProvider } from "@/lib/sessionVertical";
import type { Vertical } from "@/lib/verticalCopy";

/**
 * Pauses/resumes an already-injected Clarity session across client-side
 * (no full-reload) route changes — see `syncClarityWithRoute` in
 * `src/lib/clarity.ts` (#624). AppShell is the one place that wraps every
 * route, so it is the single chokepoint for this, same reasoning CLAUDE.md
 * already gives for theming here.
 */
function useClarityRouteSync() {
  const { pathname } = useLocation();
  useEffect(() => {
    syncClarityWithRoute(pathname);
  }, [pathname]);
}

/**
 * Live-mode app shell. The co-branded banner now ships on every route —
 * applicant/voucher flows, operator dashboard, and login — on a soft,
 * website-like background. Footer is unchanged.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  useClarityRouteSync();
  const { pathname } = useLocation();
  const { tenant, loading: tenantLoading } = useTenant();
  const { user } = useOptionalAuth();
  // Published by the applicant or voucher page once its session has answered
  // (`lib/sessionVertical.ts`); undefined until then.
  const [sessionVertical, setSessionVertical] = useState<Vertical | null | undefined>(undefined);
  const demoPartnerKey = demoPartnerKeyForPath(pathname, Boolean(user));
  // `DEMO_SURFACES_IN_BUILD` first, so a production build drops the demo
  // branches below (the review walkthrough's preview nav among them).
  const isArticleRoute = DEMO_SURFACES_IN_BUILD && demoPartnerKey === "article";
  const featherRoute = DEMO_SURFACES_IN_BUILD && demoPartnerKey === "feather";
  const partnerKey = featherRoute ? "feather" : isArticleRoute ? "article" : tenant?.branding?.partner_key;
  const isSessionFlow = isSessionJourneyPath(pathname);
  // Partner tenant, or a live recruiting session: the partner layout. Until
  // the session (and a tenant host's tenant) has answered: a neutral shell.
  // See lib/flowPresentation.ts.
  const flowLook = flowLookFor({ partnerKey, isSessionFlow, tenantLoading, sessionVertical });
  const isPartnerPresentation = flowLook === "partner";
  const isApplicantOrVoucherFlow =
    pathname === "/article/applicant-flow" ||
    pathname === "/article/voucher-flow" ||
    pathname === "/feather/applicant-flow" ||
    pathname === "/feather/voucher-flow" ||
    pathname.startsWith("/verify/") ||
    pathname === "/voucher-flow" ||
    pathname.startsWith("/voucher-flow/");
  const isPartnerFlow = isPartnerPresentation && (isApplicantOrVoucherFlow || featherRoute);
  const isPendingFlow = flowLook === "pending";
  // The partner layout's palette: Article's or Feather's for those tenants,
  // DigaVouch's own for every other org that gets the layout.
  const partnerClasses = `tenant-article${partnerKey === "feather" ? " tenant-feather" : isPartnerLookKey(partnerKey) ? "" : " tenant-digavouch"}`;
  const envConfig = {
    staging: { label: "Staging", className: "bg-trust-amber/20 text-trust-amber border-trust-amber/40" },
    uat: { label: "UAT", className: "bg-trust-blue/20 text-trust-blue border-trust-blue/40" },
    production: { label: "Production", className: "bg-trust-green/20 text-trust-green border-trust-green/40" },
  };
  const env = envConfig[APP_ENV];

  // The Article job and application pages reproduce the upstream careers
  // surface and therefore own their complete chrome. The token-backed
  // applicant and voucher routes continue through the shared app shell.
  if (isArticleRoute && pathname.startsWith("/article/jobs/")) {
    return <>{children}</>;
  }

  // The page's look is not known yet (see `flowLook` above): a plain white
  // page holding the page's own loading state, with no header, footer,
  // environment badge or cookie banner, so a recruiting applicant never sees the default chrome
  // first and a leasing one never sees the partner chrome first. One element
  // tree for every state, with the chrome left out rather than a second tree:
  // a second tree would remount the page, whose unmount clears what it
  // published, and the shell would never leave this state.
  const shellClass = isPendingFlow
    ? "app-shell-pending bg-white"
    : isPartnerFlow
      ? `${partnerClasses} article-app-canvas article-public-flow`
      : "overflow-hidden bg-[radial-gradient(circle_at_top_left,rgba(124,58,237,0.10),transparent_28%),radial-gradient(circle_at_top_right,rgba(56,189,248,0.08),transparent_24%),linear-gradient(180deg,#faf7ff_0%,#fbfdff_40%,#f8fafc_100%)]";

  return (
    <div className={`min-h-screen w-full flex flex-col relative ${shellClass}`}>
      {/* Skip link — visible on focus, jumps keyboard users past header/nav. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
        Skip to main content
      </a>

      {!isPendingFlow && <CoBrandedHeader compactArticleFlow={isPartnerFlow} />}

      {/* "Viewing <Org> as DigaVouch staff" — every route, for as long as a
          staff member is switched into an org that is not their own. Renders
          nothing otherwise (and nothing without the StaffOrgProvider).
          Not on the Feather demo pages (Juan, 2026-10-14): the presenter is
          showing an applicant's view, and the header's Home link goes back to
          the dashboard, where this banner and the org switcher are. */}
      {!featherRoute && <StaffOrgBanner />}

      {featherRoute && <REVIEW_PAGES.featherPreviewNav />}

      <main id="main-content" className="article-app-main relative z-10 flex-1 w-full">
        <div className="article-app-content px-4 sm:px-6 lg:px-10 py-6 sm:py-8 max-w-7xl mx-auto w-full">
          <SessionVerticalPublishProvider value={setSessionVertical}>
            {children}
          </SessionVerticalPublishProvider>
        </div>
      </main>

      {!isPendingFlow && <BrandFooter compactArticleFlow={isPartnerFlow} />}

      {!isPendingFlow && (
        <div className="app-environment-badge fixed top-20 right-4 z-50">
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 font-medium ${env.className}`}>
            ● {env.label}
          </Badge>
        </div>
      )}

      {/* Styled per look, so it waits for the look like the rest of the chrome. */}
      {!isPendingFlow && <CookieConsent />}
    </div>
  );
}
